import toast from 'react-hot-toast';
import Icon from "@material-tailwind/react/Icon";
import Swal from "sweetalert2";
import { useState, useEffect } from "react";
import Select from "react-select";

import { Redirect } from "react-router-dom";
import { checkUser } from "helpers/Session";
import API, { getApiErrorMessage } from "../helpers/api";

const ATTENDANCE_OPTIONS = [
  { value: "hadir", label: "Hadir" },
  { value: "surat_sakit", label: "Surat Sakit" },
  { value: "sakit", label: "Sakit" },
  { value: "izin", label: "Izin" },
  { value: "alpha", label: "Alpha" },
];

const normalizeAttendanceStatus = (status) => {
  if (status === true || status === 1 || status === "1") return "hadir";
  if (status === false || status === 0 || status === "0") return "alpha";

  const normalized = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (normalized === "true" || normalized === "hadir") return "hadir";
  if (normalized === "false" || normalized === "tidak_hadir") return "alpha";
  if (ATTENDANCE_OPTIONS.some((option) => option.value === normalized)) {
    return normalized;
  }
  return "";
};

export const isIreJabatan = (jabatan) => Number(jabatan) === 9;

export const shouldRequireFeedbackCLevel = (jabatan, feedbackCLevel) =>
  isIreJabatan(jabatan) && String(feedbackCLevel || "").trim() === "";

export const buildRaporPayload = ({
  user,
  raporKe,
  hobi,
  keterangan,
  motivasi,
  feedbackCLevel,
  jabatan,
  kehadiran,
  nilai,
}) => {
  const payload = {
    nim: user.nim,
    rapor_ke: raporKe,
    hobi: hobi,
    keterangan_absen: keterangan,
    motivasi: motivasi,
    kehadiran,
    nilai,
  };

  if (isIreJabatan(jabatan)) {
    payload.feedback_c_level = feedbackCLevel;
  }

  return payload;
};

const Rapor = () => {
  const [rapor, setRapor] = useState([]);
  const [dummy, setDummy] = useState([]);
  const [user, setUser] = useState("");
  const [raporKe, setRaporKe] = useState(0);
  const [hobi, setHobi] = useState("");
  const [allKementerian, setAllKementerian] = useState([]);
  const [jabatan, setJabatan] = useState(0);
  const [userByKementerian, setUserByKementerian] = useState([]);
  const [kementerian, setKementerian] = useState(0);
  const [detailRapor, setDetailRapor] = useState([]);
  const [kegiatanByKementerian, setKegiatanByKementerian] = useState([]);
  const [loadingKegiatan, setLoadingKegiatan] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [motivasi, setMotivasi] = useState("");
  const [feedbackCLevel, setFeedbackCLevel] = useState("");
  const [keterangan, setKeterangan] = useState("");
  const [kehadiran, setKehadiran] = useState([]);
  const [id, setId] = useState(0);
  const [loadRapor, setLoadingRapor] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [openTab, setOpenTab] = useState(1);
  const [valid, setValid] = useState(false);

  const isValidScore = (nilai) => {
    const parsed = Number(nilai);
    return nilai !== "" && Number.isFinite(parsed) && parsed >= 1 && parsed <= 100;
  };

  const buildNilaiPayload = () =>
    detailRapor.flatMap((item) =>
      (item.sub_aspek || []).map((sub) => ({
        id_subaspek: sub.id_subaspek,
        nilai: Number(sub.nilai),
      })),
    );

  const buildKehadiranPayload = () =>
    kehadiran.map((item) => ({
      id_kegiatan: item.id_kegiatan,
      status: normalizeAttendanceStatus(item.status),
    }));

  useEffect(() => {
    if (!loadRapor) return;
    (async () => {
      await API.get("/rapor")
        .then((res) => {
          const raporData = Array.isArray(res.data.data) ? res.data.data : [];
          setRapor(raporData);
          setDummy(raporData);
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Terjadi kesalahan",
            text: "Silahkan hubungi PIT!",
            footer: "Error: " + err.message,
          });
        })
        .finally(() => {
          setLoadingRapor(false);
        });
    })();
  }, [loadRapor]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [kementerianRes] = await Promise.all([API.get("/kementerian")]);
        setAllKementerian(
          Array.isArray(kementerianRes.data.data) ? kementerianRes.data.data : [],
        );
      } catch (_error) {
        toast.error("Data kegiatan gagal dimuat");
      }
    };

    fetchData();
  }, []);

  const addRapor = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await API.post(
        "/rapor",
        buildRaporPayload({
          user,
          raporKe,
          hobi,
          keterangan,
          motivasi,
          feedbackCLevel,
          jabatan,
          kehadiran: buildKehadiranPayload(),
          nilai: buildNilaiPayload(),
        }),
      );
      if (response.status === 200) {
        toast.success("Data berhasil ditambahkan");
        setLoadingRapor(true);
        setShowAddModal(false);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal menambahkan data!",
        text: getApiErrorMessage(error, "Data gagal ditambahkan"),
      });
    } finally {
      setLoading(false);
    }
  };
  const updateRapor = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await API.put(
        "/rapor/" + id,
        buildRaporPayload({
          user,
          raporKe,
          hobi,
          keterangan,
          motivasi,
          feedbackCLevel,
          jabatan,
          kehadiran: buildKehadiranPayload(),
          nilai: buildNilaiPayload(),
        }),
      );
      if (response.status === 200) {
        toast.success("Data berhasil diubah");
        setLoadingRapor(true);
        setShowEditModal(false);
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const deleteRapor = (item) => {
    const id = item.id_rapor;

    Swal.fire({
      title: "Apakah kamu yakin?",
      text: "Rapor dan data absensi kegiatan pada rapor ini akan dihapus.",
      icon: "warning",
      showCancelButton: true,
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await API.delete(`/rapor/${id}`);

          if (response.status === 200) {
            return response.data;
          }

          throw new Error(response.data);
        } catch (error) {
          
          throw error;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    })
      .then((res) => {
        if (res.isConfirmed) {
          toast.success("Data berhasil dihapus");
          setLoadingRapor(true);
        }
      })
      .catch((error) => {
        Swal.fire(
          "Error",
          "Terjadi kesalahan: " + getApiErrorMessage(error, error.message),
          "error",
        );
      });
  };

  const search = (params) => {
    if (params === "") {
      setRapor(dummy);
    } else {
      let result = dummy.filter(
        (item) =>
          (item.user?.nama || "").toLowerCase().includes(params.toLowerCase()) ||
          (item.user?.kementerian?.singkatan || "")
            .toLowerCase()
            .includes(params.toLowerCase()) ||
          (item.user?.jabatan?.jabatan || "")
            .toLowerCase()
            .includes(params.toLowerCase()),
      );
      setRapor(result);
    }
  };

  const resetForm = () => {
    setId("");
    setUser("");
    setRaporKe(0);
    setHobi("");
    setMotivasi("");
    setFeedbackCLevel("");
    setKeterangan("");
    setKehadiran([]);
    setKementerian(0);
    setJabatan(0);
    setKegiatanByKementerian([]);
    setLoadingKegiatan(false);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    resetForm();
  };

  useEffect(() => {
    if (!showAddModal && !showEditModal) {
      resetForm();
    }
  }, [showAddModal, showEditModal]);

  useEffect(() => {
    if (Number(kementerian) === 0 || Number(raporKe) === 0) return;
    if (Number(kementerian) === 12) {
      setJabatan(4);
      return null;
    }
    const fetchData = async () => {
      try {
        setLoadingKegiatan(true);
        const response = await API.get(
          `/kementerian/${kementerian}/kegiatan/rapor/${raporKe}`,
        );
        const kegiatanData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setKegiatanByKementerian(kegiatanData);
        // set kehadiran with kegiatanbykementerian value an add status on every item
	        setKehadiran(
	          kegiatanData.map((item) => ({
	            ...item,
	            status: normalizeAttendanceStatus(item.status),
	          })),
	        );
        setLoadingKegiatan(false);
      } catch (_error) {
        toast.error("Data kegiatan gagal dimuat");
      }
    };
    fetchData();
  }, [kementerian, raporKe]);

  useEffect(() => {
    if (Number(jabatan) === 0) return;
    const fetchAspek = async () => {
      try {
        const response = await API.get(`/jabatan/${jabatan}/aspek`);
        const aspekData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setDetailRapor(
          aspekData.map((item) => {
            return {
              id_aspek: item.id_aspek,
              aspek: item.aspek,
              sub_aspek: (Array.isArray(item.sub_aspek) ? item.sub_aspek : []).map((sub) => ({
                deskripsi: sub.deskripsi,
                sub_aspek: sub.sub_aspek,
                nilai: "",
                id_subaspek: sub.id_subaspek,
              })),
            };
          }),
        );
      } catch (_error) {
        toast.error("Data aspek gagal dimuat");
      }
    };
    fetchAspek();
  }, [jabatan]);

  useEffect(() => {
    if (Number(jabatan) === 0) return;
    if (Number(kementerian) === 0) return;
    const fetchData = async () => {
      try {
        setLoadingUser(true);
        const response = await API.get(
          `/users/kementerian/${kementerian}/jabatan/${jabatan}`,
        );
        const userData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setUserByKementerian(userData);
        setLoadingUser(false);
      } catch (_error) {
        toast.error("Data anggota gagal dimuat");
      }
    };
    fetchData();
  }, [kementerian, jabatan]);

  useEffect(() => {
    let pass = true;
    if (!user) pass = false;
    if (Number(raporKe) === 0) pass = false;
    if (hobi === "") pass = false;
    if (motivasi === "") pass = false;
    if (shouldRequireFeedbackCLevel(jabatan, feedbackCLevel)) pass = false;
    if (kehadiran.length === 0) pass = false;
    if (detailRapor.length === 0) pass = false;
    if (!kehadiran.every((item) => normalizeAttendanceStatus(item.status) !== "")) {
      pass = false;
    }
    if (
      !detailRapor.every((item) =>
        (item.sub_aspek || []).length > 0 &&
        (item.sub_aspek || []).every((sub) => isValidScore(sub.nilai)),
      )
    ) {
      pass = false;
    }
    if (pass) {
      setValid(true);
    } else {
      setValid(false);
    }
  }, [
    detailRapor,
    feedbackCLevel,
    hobi,
    jabatan,
    kementerian,
    kehadiran,
    motivasi,
    keterangan,
    raporKe,
    user,
  ]);

  return (
    <>
      {!checkUser() ? (
        <Redirect to={"/"} />
      ) : (
        <>
          <div className="bg-primary-light px-3 md:px-8 h-40" />

          <div className="px-3 md:px-8 h-auto -mt-36">
            <div className="container mx-auto max-w-full min-h-screen">
              <div className="grid grid-cols-1 px-4 mb-16">
                <div className="flex w-full justify-between my-4">
                  <div className="self-center">
                    <button className="bg-success text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-success/90 transition shadow-sm"
                      onClick={(e) => {
                        setShowAddModal(true);
                      }}
                    >
                      <Icon name="add" size="xl" /> Tambah Rapor
                    </button>
                  </div>
                  <div className="self-center">
                    <div className="pt-2 relative mx-auto text-gray">
                      <input
                        className="bg-white h-10 px-5 pr-16 rounded-lg text-sm focus:outline-none"
                        type="text"
                        name="search"
                        placeholder="Cari Sesuatu..."
                        onChange={(e) => {
                          search(e.target.value);
                        }}
                      />
                      <button
                        type="submit"
                        className="absolute right-0 top-0 mt-5 mr-4"
                        disabled
                      >
                        <Icon name="search" size="xl" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="glass-card flex flex-col min-w-0 break-words bg-white w-full shadow-soft rounded-2xl">
                  <div className="p-6">
                    <div className="overflow-x-auto overflow-y-auto max-h-screen">
                      <table className="relative items-center w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="sticky top-0 bg-white px-2 text-primary align-middle border-b border-solid border-gray-200 py-3 text-sm whitespace-nowrap font-semibold text-left">
                              No
                            </th>
                            <th className="sticky top-0 bg-white px-2 text-primary align-middle border-b border-solid border-gray-200 py-3 text-sm whitespace-nowrap font-semibold text-left">
                              Nama
                            </th>
                            <th className="sticky top-0 bg-white px-2 text-primary align-middle border-b border-solid border-gray-200 py-3 text-sm whitespace-nowrap font-semibold text-left">
                              Kementerian
                            </th>
                            <th className="sticky top-0 bg-white px-2 text-primary align-middle border-b border-solid border-gray-200 py-3 text-sm whitespace-nowrap font-semibold text-left">
                              Jabatan
                            </th>
                            <th className="sticky top-0 bg-white px-2 text-primary align-middle border-b border-solid border-gray-200 py-3 text-sm whitespace-nowrap font-semibold text-left">
                              Keterangan
                            </th>
                            <th className="sticky top-0 bg-white px-2 text-primary align-middle border-b border-solid border-gray-200 py-3 text-sm whitespace-nowrap font-semibold text-center">
                              Aksi
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {!loadRapor ? (
                            rapor.length > 0 ? (
                              rapor.map((item, index) => {
                                return (
                                  <tr key={index}>
                                    <th className="border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-4 text-left">
                                      {index + 1}
                                    </th>
                                    <th className="border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-4 text-left capitalize">
                                      {(item.user?.nama || "-").toLowerCase()}
                                    </th>
                                    <th className="border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-4 text-left">
                                      {item.user?.kementerian?.singkatan || "-"}
                                    </th>
                                    <th className="border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-4 text-left">
                                      {item.user?.jabatan?.jabatan || "-"}
                                    </th>
                                    <th className="border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-4 text-left">
                                      Rapor {item.rapor_ke}
                                    </th>
                                    <th className="border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-4 text-left">
                                      <div className="flex justify-evenly">
                                        <a
                                          href={
                                            "./#/detailRapor/" +
                                            item.rapor_ke +
                                            "/" +
                                            item.nim
                                          }
                                          rel="noreferrer noopener"
                                        >
                                          <button className="bg-primary text-white p-2 rounded-lg hover:bg-primary-dark transition shadow-sm"
                                          >
                                            <Icon name="visibility" size="xl" />
                                          </button>
                                        </a>
                                        <button className="bg-danger/10 text-danger p-2 rounded-lg hover:bg-danger hover:text-white transition shadow-sm group"
                                          onClick={(e) => {
                                            deleteRapor(item);
                                          }}
                                        >
                                          <Icon name="delete" size="xl" />
                                        </button>
                                      </div>
                                    </th>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-4 text-center"
                                >
                                  Tidak Ada Data
                                </td>
                              </tr>
                            )
                          ) : (
                            <tr>
                              <td
                                colSpan={6}
                                className="border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-4 text-center animate-pulse"
                              >
                                Memuat Data...
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showAddModal && (
    <div className="fixed inset-0 z-50 flex justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none bg-text-heading/50 backdrop-blur-sm transition-all">
      <div className="relative w-full max-w-lg mx-auto my-6">
        <div className="border-0 rounded-2xl shadow-xl relative flex flex-col w-full bg-white outline-none focus:outline-none">
          <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t-2xl">
            <h3 className="text-xl font-bold text-text-heading">Tambah Rapor</h3>
            <button className="p-1 ml-auto bg-transparent border-0 text-text-muted float-right text-3xl leading-none font-semibold outline-none focus:outline-none hover:text-danger" onClick={() => closeModal()}>×</button>
          </div>
          <div className="relative p-6 flex-auto">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  addRapor();
                }}
              >
                <div className="flex flex-wrap">
                  <div className="w-full">
                    <ul
                      className="flex mb-0 list-none flex-wrap pt-3 pb-4 flex-row w-full"
                      role="tablist"
                    >
                      <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
                        <a
                          className={
                            "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                            (openTab === 1
                              ? "text-white bg-primary-light"
                              : "text-sgeOcean bg-sgeCream")
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenTab(1);
                          }}
                          data-toggle="tab"
                          href="#link1"
                          role="tablist"
                        >
                          Data Diri
                        </a>
                      </li>
                      <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
                        <a
                          className={
                            "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                            (openTab === 2
                              ? "text-white bg-primary-light"
                              : "text-sgeOcean bg-sgeCream")
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenTab(2);
                          }}
                          data-toggle="tab"
                          href="#link2"
                          role="tablist"
                        >
                          Absensi
                        </a>
                      </li>
                      <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
                        <a
                          className={
                            "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                            (openTab === 3
                              ? "text-white bg-primary-light"
                              : "text-sgeOcean bg-sgeCream")
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenTab(3);
                          }}
                          data-toggle="tab"
                          href="#link3"
                          role="tablist"
                        >
                          Penilaian
                        </a>
                      </li>
                    </ul>
                    <div className="flex flex-wrap break-words bg-white mb-3 mt-3 flex-col">
                      <div
                        className={openTab === 1 ? "block" : "hidden"}
                        id="link1"
                      >
                        <div className="grid grid-cols-2 gap-8">
                          <div className="w-full mb-3">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                              Kementerian
                            </label>
                            <select
                              className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                              value={kementerian}
                              onChange={(e) => {
                                setKementerian(e.target.value);
                                setJabatan("");
                                setUser("");
                              }}
                              required
                            >
                              <option value={0}>Pilih Kementerian</option>
                              {allKementerian ? (
                                allKementerian.map((item, index) => {
                                  return (
                                    <option
                                      key={index}
                                      value={item.id_kementerian}
                                    >
                                      {item.singkatan}
                                    </option>
                                  );
                                })
                              ) : (
                                <></>
                              )}
                            </select>
                          </div>
                          <div className="w-full mb-3">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                              Jabatan
                            </label>
                            <select
                              className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                              value={jabatan}
                              onChange={(e) => {
                                setJabatan(e.target.value);
                                setUser("");
                                setDetailRapor([]);
                              }}
                              required
                            >
                              <option value={0}>Pilih Jabatan</option>
                              <option value={1}>Staff</option>
                              <option value={2}>C-Level</option>
                              <option value={9}>Ire</option>
                              <option value={4}>Director</option>
                              <option value={5}>Cabinet Secretary</option>
                              <option value={6}>Cabinet Advisor</option>
                              <option value={8}>President</option>
                              <option value={7}>Vice President</option>
                            </select>
                          </div>
                        </div>
                        <div className="w-full mb-3">
                          <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                            Nama
                          </label>
                          <Select menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            options={userByKementerian}
                            onChange={(e) => {
                              setUser(e);
                            }}
                            value={user}
                            getOptionLabel={(option) => option.nama}
                            getOptionValue={(option) => option.nim}
                            isDisabled={loadingUser === true}
                            isLoading={loadingUser}
                            placeholder="Pilih Nama..."
                            defaultValue={"-"}
                            required
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-8">
                          <div className="w-full mb-3">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                              Rapor Ke
                            </label>
                            <select
                              className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                              required
                              onChange={(e) => {
                                setRaporKe(e.target.value);
                              }}
                              value={raporKe}
                            >
                              <option value={0}>Pilih Urutan Rapor</option>
                              <option value={1}>1</option>
                              <option value={2}>2</option>
                              <option value={3}>3</option>
                            </select>
                          </div>
                          <div className="w-full mb-3">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                              Hobi
                            </label>
                            <input
                              className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                              type="text"
                              placeholder="Masukkan Hobi..."
                              required
                              value={hobi}
                              onChange={(e) => {
                                setHobi(e.target.value);
                              }}
                            />
                          </div>
                          <div className="w-full mb-3">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                              Activity Reflection
                            </label>
                            <textarea
                              className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                              placeholder="Masukkan Activity Reflection..."
                              required
                              rows={4}
                              value={motivasi}
                              onChange={(e) => {
                                setMotivasi(e.target.value);
                              }}
                            ></textarea>
                          </div>
                          {isIreJabatan(jabatan) && (
                            <div className="w-full mb-3">
                              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                Feedback C-Level
                              </label>
                              <textarea
                                className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                placeholder="Masukkan Feedback C-Level..."
                                required
                                rows={4}
                                value={feedbackCLevel}
                                onChange={(e) => {
                                  setFeedbackCLevel(e.target.value);
                                }}
                              ></textarea>
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        className={openTab === 2 ? "block" : "hidden"}
                        id="link2"
                      >
                        {loadingKegiatan ||
                        kegiatanByKementerian.length === 0 ? (
                          <div className="flex justify-between">
                            <div className="animate-pulse bg-primary opacity-10 rounded h-6"></div>
                            <div className="animate-pulse bg-primary opacity-10 rounded h-6"></div>
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between">
                              <p className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                Kegiatan
                              </p>
                              <p className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                Kehadiran
                              </p>
                            </div>

                            {kegiatanByKementerian.map((item, index) => {
                              return (
                                <div key={index}>
                                  <div className="w-full my-3 flex justify-between items-center gap-x-12">
                                    <p className="block tracking-wide font-medium flex-grow">
                                      {item.kegiatan || "-"}
                                    </p>
                                    <select
                                      className="form-select h-10 px-2 border rounded"
                                      value={normalizeAttendanceStatus(
                                        kehadiran[index]?.status,
                                      )}
                                      onChange={(e) => {
                                        const newKehadiran = [...kehadiran];
                                        newKehadiran[index] = {
                                          ...newKehadiran[index],
                                          status: e.target.value,
                                        };
                                        setKehadiran(newKehadiran);
                                      }}
                                      required
                                    >
                                      <option value="" disabled>
                                        Pilih Status
                                      </option>
                                      {ATTENDANCE_OPTIONS.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <hr />
                                </div>
                              );
                            })}
                            <div className="w-full mb-3 mt-10">
                              <label className="block tracking-wide text-gray-700 text-md font-bold mb-2">
                                Keterangan
                              </label>
                              <textarea
                                className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                placeholder="Masukkan Keterangan Kehadiran..."
                                rows={4}
                                value={keterangan}
                                onChange={(e) => {
                                  setKeterangan(e.target.value);
                                }}
                              ></textarea>
                            </div>
                          </>
                        )}
                      </div>
                      {/* //POST */}
                      <div
                        className={openTab === 3 ? "block" : "hidden"}
                        id="link3"
                      >
                        {detailRapor.map((item, index) => {
                          return (
                            <div key={index} className="grid gap-5">
                              <div className="w-full">
                                <h1 className="block uppercase tracking-wide text-gray-700 text-sm font-bold">
                                  {index + 1}. Aspek {item.aspek || ""}
                                </h1>
                              </div>
                              <div className="w-full mb-3">
                                {(item.sub_aspek || []).map((subItem, subIndex) => (
                                  <div key={subIndex} className="w-full mb-3">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                      Sub Aspek {subIndex + 1}:{" "}
                                      {subItem.sub_aspek}
                                    </label>
                                    <p className="text-gray-700 text-xs mb-2">
                                      {subItem.deskripsi}
                                    </p>
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                      Hasil (1-100)
                                    </label>
                                    <input
                                      type="number"
                                      min="1"
                                      max="100"
                                      step="0.01"
                                      className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                      placeholder="Masukkan nilai sub aspek..."
                                      value={subItem.nilai}
                                      onChange={(e) => {
                                        const nilai = e.target.value;
                                        const newSubAspek = (item.sub_aspek || []).map(
                                          (sub, idx) => {
                                            if (idx === subIndex) {
                                              return {
                                                ...sub,
                                                nilai,
                                              };
                                            }
                                            return sub;
                                          },
                                        );
                                        const newDetailRapor = detailRapor.map(
                                          (detItem) => {
                                            if (
                                              detItem.id_aspek === item.id_aspek
                                            ) {
                                              return {
                                                ...detItem,
                                                sub_aspek: newSubAspek,
                                              };
                                            }
                                            return detItem;
                                          },
                                        );
                                        setDetailRapor(newDetailRapor);
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="text-white bg-primary focus:ring-4 focus:outline-none focus:ring-primary/50 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-30 disabled:pointer-events-none"
                        disabled={loading || !valid}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
        </div>
      </div>
    </div>
  )}
          {/* ubah update */}
          {showEditModal && (
            <>
              {showEditModal && (
    <div className="fixed inset-0 z-50 flex justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none bg-text-heading/50 backdrop-blur-sm transition-all">
      <div className="relative w-full max-w-lg mx-auto my-6">
        <div className="border-0 rounded-2xl shadow-xl relative flex flex-col w-full bg-white outline-none focus:outline-none">
          <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t-2xl">
            <h3 className="text-xl font-bold text-text-heading">
                  Ubah Rapor</h3>
            <button className="p-1 ml-auto bg-transparent border-0 text-text-muted float-right text-3xl leading-none font-semibold outline-none focus:outline-none hover:text-danger" onClick={() => closeModal()}>×</button>
          </div>
          <div className="relative p-6 flex-auto">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      updateRapor();
                    }}
                  >
                    <div className="flex flex-wrap">
                      <div className="w-full">
                        <ul
                          className="flex mb-0 list-none flex-wrap pt-3 pb-4 flex-row w-full"
                          role="tablist"
                        >
                          <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
                            <a
                              className={
                                "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                                (openTab === 1
                                  ? "text-white bg-primary-light"
                                  : "text-sgeOcean bg-sgeCream")
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                setOpenTab(1);
                              }}
                              data-toggle="tab"
                              href="#link1"
                              role="tablist"
                            >
                              Detail User
                            </a>
                          </li>
                          <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
                            <a
                              className={
                                "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                                (openTab === 2
                                  ? "text-white bg-primary-light"
                                  : "text-sgeOcean bg-sgeCream")
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                setOpenTab(2);
                              }}
                              data-toggle="tab"
                              href="#link2"
                              role="tablist"
                            >
                              Kehadiran
                            </a>
                          </li>
                          <li className="-mb-px mr-2 last:mr-0 flex-auto text-center">
                            <a
                              className={
                                "text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal " +
                                (openTab === 3
                                  ? "text-white bg-primary-light"
                                  : "text-sgeOcean bg-sgeCream")
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                setOpenTab(3);
                              }}
                              data-toggle="tab"
                              href="#link3"
                              role="tablist"
                            >
                              Penilaian
                            </a>
                          </li>
                        </ul>
                        <div className="flex flex-wrap break-words bg-white mb-3 mt-3 flex-col">
                          <div
                            className={openTab === 1 ? "block" : "hidden"}
                            id="link1"
                          >
                            <div className="w-full mb-3">
                              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                Nama
                              </label>
                              <input
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                value={user?.user?.nama || user?.nama || "-"}
                                disabled
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-8">
                              <div className="w-full mb-3">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                  Rapor Ke
                                </label>
                                <select
                                  className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                  required
                                  onChange={(e) => {
                                    setRaporKe(e.target.value);
                                  }}
                                  value={raporKe}
                                >
                                  <option value={0}>Pilih Urutan Rapor</option>
                                  <option value={1}>1</option>
                                  <option value={2}>2</option>
                                  <option value={3}>3</option>
                                </select>
                              </div>
                              <div className="w-full mb-3">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                  Hobi
                                </label>
                                <input
                                  className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                  type="text"
                                  placeholder="Masukkan Hobi..."
                                  required
                                  value={hobi}
                                  onChange={(e) => {
                                    setHobi(e.target.value);
                                  }}
                                />
                              </div>
                              <div className="w-full mb-3">
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                  Activity Reflection
                                </label>
                                <textarea
                                  className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                  placeholder="Masukkan Activity Reflection..."
                                  required
                                  rows={4}
                                  value={motivasi}
                                  onChange={(e) => {
                                    setMotivasi(e.target.value);
                                  }}
                                ></textarea>
                              </div>
                              {isIreJabatan(jabatan) && (
                                <div className="w-full mb-3">
                                  <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                    Feedback C-Level
                                  </label>
                                  <textarea
                                    className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                    placeholder="Masukkan Feedback C-Level..."
                                    required
                                    rows={4}
                                    value={feedbackCLevel}
                                    onChange={(e) => {
                                      setFeedbackCLevel(e.target.value);
                                    }}
                                  ></textarea>
                                </div>
                              )}
                              <div className="w-full mb-3">
                                <label className="block tracking-wide text-gray-700 text-md font-bold mb-2">
                                  Keterangan
                                </label>
                                <textarea
                                  className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
                                  placeholder="Masukkan Keterangan Kehadiran..."
                                  rows={4}
                                  value={keterangan}
                                  onChange={(e) => {
                                    setKeterangan(e.target.value);
                                  }}
                                ></textarea>
                              </div>
                            </div>
                          </div>
                          <div
                            className={openTab === 2 ? "block" : "hidden"}
                            id="link2"
                          >
                            <div className="flex justify-between">
                              <p className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                Kegiatan
                              </p>
                              <p className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                Kehadiran
                              </p>
                            </div>
                            {/* Update */}
                            {kehadiran.map((item, index) => {
                              return (
                                <div key={index}>
                                  <div className="w-full my-3 flex justify-between items-center gap-x-12">
                                    <p className="block tracking-wide font-medium flex-grow">
                                      {item.kegiatan?.kegiatan || item.kegiatan || "-"}
                                    </p>
                                    <select
                                      className="form-select h-10 px-2 border rounded"
                                      value={normalizeAttendanceStatus(item.status)}
                                      onChange={(e) => {
                                        const id_kegiatan = item.id_kegiatan;
                                        const newKehadiran = kehadiran.map(
                                          (k) => {
                                            if (k.id_kegiatan === id_kegiatan) {
                                              return {
                                                ...k,
                                                status: e.target.value,
                                              };
                                            }
                                            return k;
                                          },
                                        );
                                        setKehadiran(newKehadiran);
                                      }}
                                      required
                                    >
                                      <option value="" disabled>
                                        Pilih Status
                                      </option>
                                      {ATTENDANCE_OPTIONS.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <hr />
                                </div>
                              );
                            })}
                          </div>
                          <div
                            className={openTab === 3 ? "block" : "hidden"}
                            id="link3"
                          >
	                            {detailRapor.map((item, index) => {
	                              return (
	                                <div key={index} className="grid gap-5">
	                                  <div className="w-full">
	                                    <h1 className="block uppercase tracking-wide text-gray-700 text-sm font-bold">
	                                      {index + 1}. Aspek{" "}
	                                      {item.aspek?.aspek || item.aspek || ""}
	                                    </h1>
	                                  </div>
	                                  <div className="w-full mb-3">
	                                    {(item.sub_aspek || []).map(
	                                      (subItem, subIndex) => (
	                                        <div
	                                          key={subIndex}
	                                          className="w-full mb-3"
	                                        >
	                                          <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
	                                            Sub Aspek {subIndex + 1}:{" "}
	                                            {subItem.sub_aspek}
	                                          </label>
	                                          <p className="text-gray-700 text-xs mb-2">
	                                            {subItem.deskripsi}
	                                          </p>
	                                          <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
	                                            Hasil (1-100)
	                                          </label>
	                                          <input
	                                            type="number"
	                                            min="1"
	                                            max="100"
	                                            step="0.01"
	                                            className="w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/50"
	                                            placeholder="Masukkan nilai sub aspek..."
	                                            value={subItem.nilai}
	                                            onChange={(e) => {
	                                              const nilai = e.target.value;
	                                              const newSubAspek =
	                                                (item.sub_aspek || []).map(
	                                                  (sub, idx) => {
	                                                    if (idx === subIndex) {
	                                                      return {
	                                                        ...sub,
	                                                        nilai,
	                                                      };
	                                                    }
	                                                    return sub;
	                                                  },
	                                                );
	                                              const newDetailRapor =
	                                                detailRapor.map((detItem) => {
	                                                  if (
	                                                    detItem.id_aspek ===
	                                                    item.id_aspek
	                                                  ) {
	                                                    return {
	                                                      ...detItem,
	                                                      sub_aspek: newSubAspek,
	                                                    };
	                                                  }
	                                                  return detItem;
	                                                });
	                                              setDetailRapor(newDetailRapor);
	                                            }}
	                                          />
	                                        </div>
	                                      ),
	                                    )}
	                                  </div>
	                                </div>
	                              );
                            })}
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            className="text-white bg-primary focus:ring-4 focus:outline-none focus:ring-primary/50 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-30 disabled:pointer-events-none"
                            disabled={loading || !valid}
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
        </div>
      </div>
    </div>
  )}
              
            </>
          )}
        </>
      )}
    </>
  );
};

export default Rapor;
